import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import moment from 'moment';
import Ipms from '../../images/ipms.svg';
import { API } from '../../apis/api';
import { API_RESOURCE_URLS, ASN_STATUS } from '../../constants';
import './InformationRadiator.css';
import { useAjaxCallbackWithIntervals } from '../../hooks';
import {
  getFormattedAsn,
  splitDescription,
  compareByDateAndSerialNumber,
  getSelectedOptionalColumns,
} from '../../utils/tableUtils';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import LineDropdownSelector from '../LineDropdownSelector/LineDropdownSelector';
import AttributesSelector from '../AttributesSelector/AttributesSelector';
import { optionalColumns, OPTIONAL_ATTRIBUTES } from '../../staticData';
import { Link, useLocation } from 'react-router-dom';
import { buildErrorMessage } from '../../apis/calls';

const InformationRadiator = () => {
  const location = useLocation();

  const DROPPED_ROWS_COUNT = 30;

  // --- Default attribute columns: Power Steering, Colour, Country ---
  const DEFAULT_ATTRIBUTE_COLS = [
    OPTIONAL_ATTRIBUTES.POWER_STEERING,
    OPTIONAL_ATTRIBUTES.COLOUR,
    OPTIONAL_ATTRIBUTES.COUNTRY,
  ];

  const [selectedOptionalColNames, setSelectedOptionalColNames] = useState(DEFAULT_ATTRIBUTE_COLS);

  const activeOptionalColumns = useMemo(() => {
    return getSelectedOptionalColumns(optionalColumns, selectedOptionalColNames, {});
  }, [selectedOptionalColNames]);

  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;

  const latestLineValueRef = useRef(line.value);
  const latestPlantValueRef = useRef(plant.value);

  const { showInternalError, showError } = usePopupManager();

  const [droppedRowsData, setDroppedRowsData] = useState({
    data: [],
    loading: false,
  });

  const [readyForDropRowsData, setReadyForDropRowsData] = useState({
    data: [],
    loading: false,
  });
  const isLucknowProlifePlant = plant?.label === 'Lucknow-Prolife'
  const parentName = isLucknowProlifePlant ? 'Parent Part' : 'VC'

  // --- Filter states ---
  const [selectedFromSequence, setSelectedFromSequence] = useState('');
  const [selectedToSequence, setSelectedToSequence] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(null);

  const [appliedFromSequence, setappliedFromSequence] = useState(null);
  const [appliedToSequence, setappliedToSequence] = useState(null);
  const [appliedStatus, setAppliedStatus] = useState(null);

  // --- Validation states for sequence inputs ---
  const [fromSeqError, setFromSeqError] = useState('');
  const [toSeqError, setToSeqError] = useState('');

  const validateSeqValue = (value) => {
    if (value === '') return '';
    if (!/^\d+$/.test(value)) return 'Only integers allowed';
    const num = parseInt(value, 10);
    if (num < 0 || num > 99) return 'Value must be 0-99';
    return '';
  };

  const handleFromSeqChange = (e) => {
    const val = e.target.value;
    setSelectedFromSequence(val);
    setFromSeqError(validateSeqValue(val));
  };

  const handleToSeqChange = (e) => {
    const val = e.target.value;
    setSelectedToSequence(val);
    setToSeqError(validateSeqValue(val));
  };

  // --- Dynamic Count Summary from API ---
  const [, setCountSummary] = useState({ lineup: 30, dropped: 30 });

  // --- Status filter options ---
  const statusFilterOptions = [
    { value: 'READY_FOR_DROP', label: 'Ready For Drop' },
    { value: 'DROPPED', label: 'Dropped' },
  ];

  // --- Sequence options removed: replaced dropdowns with text inputs ---

  // --- Apply filters ---
  const filteredReadyForDropData = useMemo(() => {
    return readyForDropRowsData.data || [];
  }, [readyForDropRowsData.data]);

  const filteredDroppedData = useMemo(() => {
    return droppedRowsData.data || [];
  }, [droppedRowsData.data]);

  const READY_FOR_DROP_ROWS_COUNT = filteredReadyForDropData.length;
  const FILTERED_DROPPED_ROWS_COUNT = filteredDroppedData.length || DROPPED_ROWS_COUNT;

  // --- Clear filters handler ---
  const handleClearFilters = () => {
    setSelectedFromSequence('');
    setSelectedToSequence('');
    setFromSeqError('');
    setToSeqError('');
    setSelectedStatus(null);
    setappliedFromSequence(null);
    setappliedToSequence(null);
    setAppliedStatus(null);
  };

  const handleStatusChange = (val) => {
    const statusVal = val?.value || null;
    setSelectedStatus(val);
    setSelectedFromSequence('');
    setSelectedToSequence('');
    setFromSeqError('');
    setToSeqError('');
    setappliedFromSequence(null);
    setappliedToSequence(null);
    setAppliedStatus(statusVal);
  };

  const isFilterActive =
    selectedFromSequence !== '' ||
    selectedToSequence !== '' ||
    selectedStatus !== null ||
    appliedFromSequence !== null ||
    appliedToSequence !== null ||
    appliedStatus !== null;



  const getAsnsByStatusForCount = async (status, count, optionalParams) => {
    if (plant.value == null) return;
    if (line.value == null) return;

    let url = API_RESOURCE_URLS.getDroppingUpdate(
      plant.value,
      line.value,
      status,
      count,
    );

    if (optionalParams?.from !== undefined && optionalParams?.from !== null) {
      url += `&from=${optionalParams.from}`;
    }
    if (optionalParams?.to !== undefined && optionalParams?.to !== null) {
      url += `&to=${optionalParams.to}`;
    }

    const response = await API.get(url);

    return response.data;
  };

  const prepareLastDroppedAsnRows = (asns, requiredNoOfRows) => {
    let sortDateByDesc =
      asns &&
      asns.sort((a, b) => {
        if (
          a.dropTimestamp != null &&
          a !== undefined &&
          b.dropTimestamp != null &&
          b !== undefined
        )
          return (
            new Date(...b?.dropTimestamp.split('/').reverse()) -
            new Date(...a?.dropTimestamp.split('/').reverse())
          );
        else return [];
      });

    const startPos = appliedFromSequence !== null ? appliedFromSequence : 1;
    let position = startPos - 1;
    const addPositionValue = sortDateByDesc?.map(FormatedValue => {
      position = position + 1;
      return { ...FormatedValue, position: position };
    });

    const positionToAsnMap = {};

    addPositionValue &&
      addPositionValue.forEach(asn => (positionToAsnMap[asn.position] = asn));

    const rowsInAscendingOrder = [...Array(requiredNoOfRows).keys()].map(
      (_, index) => {
        return positionToAsnMap[index + startPos];
      },
    );
    return rowsInAscendingOrder;
  };

  const loadLastDroppedAsn = async (fromSequenceNumber, toSequenceNumber) => {
    setDroppedRowsData(prevState => ({ ...prevState, loading: true }));
    try {
      const noOfRows = (fromSequenceNumber && toSequenceNumber) ? (toSequenceNumber - fromSequenceNumber + 1) : 30;

      const asns = await getAsnsByStatusForCount(ASN_STATUS.DROPPED, noOfRows, {
        from: fromSequenceNumber,
        to: toSequenceNumber,
      });
      if (
        plant.value !== latestPlantValueRef.current ||
        line.value !== latestLineValueRef.current
      )
        return setDroppedRowsData(prevState => ({
          ...prevState,
          loading: false,
        }));

      const positionallyFormattedRows = prepareLastDroppedAsnRows(
        asns,
        noOfRows,
      );

      setDroppedRowsData({
        data: positionallyFormattedRows,
        loading: false,
      });
    } catch (err) {
      console.error(
        'failed to load last dropped asn, err:' + buildErrorMessage(err),
      );
      setDroppedRowsData({
        data: [],
        loading: false,
      });
      showInternalError();
    }
  };

  const prepareReadyForDropRows = (data, requiredNoOfRows) => {
    const positionToAsnMap = {};
    data && data.forEach(asn => (positionToAsnMap[asn.position] = asn));
    const rowsInDescendingOrder = [...Array(requiredNoOfRows).keys()]
      .map((_, index) => positionToAsnMap[index + 1] || { position: index + 1 })
      .sort((a, b) => b.position - a.position);
    return rowsInDescendingOrder;
  };

  const loadFirstReadyForDroppedAsn = async (fromSequenceNumber, toSequenceNumber) => {
    setReadyForDropRowsData(prevState => ({ ...prevState, loading: true }));
    try {
      const noOfRows = (fromSequenceNumber && toSequenceNumber) ? (toSequenceNumber - fromSequenceNumber + 1) : 15;
      const asns = await getAsnsByStatusForCount(
        ASN_STATUS.READY_FOR_DROP,
        noOfRows,
        {
          from: fromSequenceNumber,
          to: toSequenceNumber,
        },
      );
      const validAsns = Array.isArray(asns) ? asns : [];
      const noOfRowsForAsn = validAsns.length;

      if (
        plant.value !== latestPlantValueRef.current ||
        line.value !== latestLineValueRef.current
      )
        return setReadyForDropRowsData(prevState => ({
          ...prevState,
          loading: false,
        }));

      const sortedData = asns?.sort((a, b) =>
        compareByDateAndSerialNumber(a, b, 'productionDate', 'sequence'),
      );

      let position = 0;
      const addPositionValue = sortedData?.map(FormatedValue => {
        position = position + 1;
        return { ...FormatedValue, position: position };
      });

      const positionallyFormattedRows = prepareReadyForDropRows(
        addPositionValue,
        noOfRowsForAsn,
      );
      setReadyForDropRowsData({
        data: positionallyFormattedRows,
        loading: false,
      });
    } catch (err) {
      console.error(
        'failed to load last ready for drop asn, err:' + buildErrorMessage(err),
      );
      setReadyForDropRowsData({
        data: [],
        loading: false,
      });
      showInternalError();
    }
  };

  const loadCountSummary = async () => {
    if (plant.value == null || line.value == null) return;
    try {
      const todayStr = moment().format('YYYY-MM-DD');
      const url = API_RESOURCE_URLS.chassisAsnCountSummary(plant.value, line.value, todayStr);
      const response = await API.get(url);
      if (response && response.data) {
        setCountSummary({
          lineup: response.data.lineup || 30,
          dropped: response.data.dropped || 30,
        });
      }
    } catch (err) {
      showInternalError();
      console.error('Failed to load count summary: ' + buildErrorMessage(err));
    }
  };

  const loadRows = (lineVal, plantVal, fromSequenceNumber, toSequenceNumber, statusVal) => {
    const promises = [];
    promises.push(loadCountSummary());

    if (!statusVal || statusVal === 'READY_FOR_DROP') {
      // Ready For Drop table always loads without sequence filters
      promises.push(loadFirstReadyForDroppedAsn(null, null));
    } else {
      setReadyForDropRowsData({ data: [], loading: false });
    }

    if (!statusVal || statusVal === 'DROPPED') {
      // Only DROPPED CHASSIS table uses From/To Seq filters
      promises.push(loadLastDroppedAsn(fromSequenceNumber, toSequenceNumber));
    } else {
      setDroppedRowsData({ data: [], loading: false });
    }

    return Promise.all(promises);
  };

  useEffect(() => {
    latestLineValueRef.current = line.value;
    latestPlantValueRef.current = plant.value;
  }, [plant.value, line.value]);

  const lastRefreshedDate = useAjaxCallbackWithIntervals(
    droppedRowsData.loading || readyForDropRowsData.loading,
    false,
    loadRows,
    line.value,
    plant.value,
    appliedFromSequence,
    appliedToSequence,
    appliedStatus,
  );

  const handleSearch = () => {
    // Validate before searching
    const fromErr = validateSeqValue(selectedFromSequence);
    const toErr = validateSeqValue(selectedToSequence);
    setFromSeqError(fromErr);
    setToSeqError(toErr);

    if (fromErr || toErr) return;

    const fromSequenceNumber = selectedFromSequence !== '' ? parseInt(selectedFromSequence, 10) : null;
    const toSequenceNumber = selectedToSequence !== '' ? parseInt(selectedToSequence, 10) : null;
    const filterStatusValue = selectedStatus?.value || null;

    if (fromSequenceNumber !== null && toSequenceNumber !== null && fromSequenceNumber > toSequenceNumber) {
      showError({
        contextText: 'Invalid Sequence Range',
        info: 'From Sequence cannot be greater than To Sequence.',
      });
      return;
    }

    setappliedFromSequence(fromSequenceNumber);
    setappliedToSequence(toSequenceNumber);
    setAppliedStatus(filterStatusValue);
  };

  const getPaddedPositionString = positionIndex =>
    String(positionIndex).padStart(2, '0');



  const getAttributeValue = (row, col) => {
    if (!row) return '-';
    const field = col.field;

    if (field === 'colourCode') {
      const desc = row.attributes?.colourDescription || row.colourDescription;
      const code = row.attributes?.colourCode || row.colourCode;
      if (desc && code) return `${desc} (${code})`;
      if (desc) return desc;
      if (code) return code;
    }

    const valFromAttr = row.attributes ? row.attributes[field] : undefined;
    const valFromRow = row[field];
    const val = valFromAttr !== undefined && valFromAttr !== null ? valFromAttr : valFromRow;

    if (val === undefined || val === null || val === '') return '-';
    return String(val);
  };

  // ===== Interactive Column Configuration =====
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const buildDefaultColumns = () => {
    const hasAttrs = activeOptionalColumns.length > 0;
    const base = [
      { id: 'asn', label: 'ASN', width: hasAttrs ? 120 : 160, align: 'center' },
      { id: 'description', label: `${parentName} Description`, width: hasAttrs ? 320 : 480, align: 'left' },
      { id: 'vc', label: parentName, width: hasAttrs ? 160 : 260, align: 'left' },
    ];
    const attrCols = activeOptionalColumns.map((col) => ({
      id: `attr_${col.field}`,
      label: col.title,
      width: 130,
      align: 'center',
      attrCol: col,
    }));
    return [...base, ...attrCols];
  };

  const [columnOrder, setColumnOrder] = useState(() => buildDefaultColumns());
  const [columnWidths, setColumnWidths] = useState(() => {
    const widths = {};
    buildDefaultColumns().forEach(c => { widths[c.id] = c.width; });
    return widths;
  });

  // Re-sync columns when activeOptionalColumns change
  const prevActiveOptColsRef = useRef(activeOptionalColumns);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const prevIds = prevActiveOptColsRef.current.map(c => c.field);
    const currIds = activeOptionalColumns.map(c => c.field);
    const changed = prevIds.length !== currIds.length || prevIds.some((id, i) => id !== currIds[i]);
    if (changed) {
      const newCols = buildDefaultColumns();
      // Preserve existing column order for base cols that still exist
      const existingIds = columnOrder.map(c => c.id);
      const kept = columnOrder.filter(c => {
        if (!c.id.startsWith('attr_')) return true;
        return newCols.some(nc => nc.id === c.id);
      });
      // Add new attr cols that didn't exist before
      const newAttrCols = newCols.filter(nc => nc.id.startsWith('attr_') && !existingIds.includes(nc.id));
      const merged = [...kept, ...newAttrCols];
      // Update labels/attrCol references
      const updated = merged.map(c => {
        const fresh = newCols.find(nc => nc.id === c.id);
        return fresh ? { ...c, label: fresh.label, attrCol: fresh.attrCol } : c;
      });
      setColumnOrder(updated);
      // Set widths for new columns
      setColumnWidths(prev => {
        const next = { ...prev };
        newAttrCols.forEach(c => { next[c.id] = c.width; });
        return next;
      });
      prevActiveOptColsRef.current = activeOptionalColumns;
    }
  }, [activeOptionalColumns, buildDefaultColumns, columnOrder]);

  // Reset columns to default
  const handleResetColumns = () => {
    const defaults = buildDefaultColumns();
    setColumnOrder(defaults);
    const widths = {};
    defaults.forEach(c => { widths[c.id] = c.width; });
    setColumnWidths(widths);
  };

  // ----- Drag-and-drop reorder -----
  const [dragColId, setDragColId] = useState(null);
  const [dragOverColId, setDragOverColId] = useState(null);

  const handleDragStart = (e, colId) => {
    setDragColId(colId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', colId);
  };

  const handleDragOver = (e, colId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (colId !== dragColId) {
      setDragOverColId(colId);
    }
  };

  const handleDragLeave = () => {
    setDragOverColId(null);
  };

  const handleDrop = (e, targetColId) => {
    e.preventDefault();
    setDragOverColId(null);
    if (!dragColId || dragColId === targetColId) {
      setDragColId(null);
      return;
    }
    setColumnOrder(prev => {
      const arr = [...prev];
      const fromIdx = arr.findIndex(c => c.id === dragColId);
      const toIdx = arr.findIndex(c => c.id === targetColId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr;
    });
    setDragColId(null);
  };

  const handleDragEnd = () => {
    setDragColId(null);
    setDragOverColId(null);
  };

  // ----- Column resize -----
  const tableRef = useRef(null);

  const handleResizeStart = (e, colId) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = columnWidths[colId] || 120;

    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      const newWidth = Math.max(60, startWidth + delta);
      setColumnWidths(prev => ({ ...prev, [colId]: newWidth }));
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // ----- Cell value extraction -----
  const getCellValue = (row, col) => {
    if (!row) return '-';
    switch (col.id) {
      case 'asn':
        return getFormattedAsn(row);
      case 'description': {
        const [head, tail] = splitDescription(row.materialDescription);
        return (
          <>
            <span className="blue">{head || '-'}</span>{' '}
            {tail || '-'}
          </>
        );
      }
      case 'vc':
        return row.materialNumber || '-';
      default:
        if (col.attrCol) return getAttributeValue(row, col.attrCol);
        return '-';
    }
  };

  // Fixed columns width
  const FIXED_STATION_WIDTH = 70;
  const FIXED_SEQ_WIDTH = 55;


  // ----- Render header row -----
  const renderInteractiveHeader = () => (
    <tr className="asnHeading">
      <td className="ir-col-fixed" style={{ width: FIXED_STATION_WIDTH, minWidth: FIXED_STATION_WIDTH, border: '1px solid #555', borderTop: '2px solid #a76100', background: '#1a1a1a', fontSize: '13px', fontWeight: 600 }}>
        &nbsp;
      </td>
      <td className="ir-col-fixed" style={{ width: FIXED_SEQ_WIDTH, minWidth: FIXED_SEQ_WIDTH, border: '1px solid #555', borderTop: '2px solid #a76100', background: '#1a1a1a', fontSize: '13px', fontWeight: 600 }}>
        &nbsp;
      </td>
      {columnOrder.map((col) => (
        <td
          key={col.id}
          className={`ir-col-header${dragColId === col.id ? ' ir-col-header--dragging' : ''}${dragOverColId === col.id ? ' ir-col-header--drag-over' : ''}`}
          style={{ width: columnWidths[col.id] || 120, minWidth: 60, textAlign: col.align || 'left' }}
          draggable
          onDragStart={(e) => handleDragStart(e, col.id)}
          onDragOver={(e) => handleDragOver(e, col.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, col.id)}
          onDragEnd={handleDragEnd}
        >
          <span className="ir-col-header-label">
            <span className="ir-col-drag-icon">⠿</span>
            {col.label}
          </span>
          <span
            className="ir-col-resize-handle"
            onMouseDown={(e) => handleResizeStart(e, col.id)}
          />
        </td>
      ))}
    </tr>
  );

  // ----- Render data row -----
  const renderInteractiveRow = (row, index, stationLabel) => {
    const startPos = appliedFromSequence !== null ? appliedFromSequence : 1;
    const position = row ? (row.position || index + startPos) : index + startPos;

    return (
      <tr key={index}>
        <td className="ir-col-fixed ir-col-cell" style={{ width: FIXED_STATION_WIDTH, textAlign: 'center' }}>
          {stationLabel}
        </td>
        <td className="ir-col-fixed ir-col-cell" style={{ width: FIXED_SEQ_WIDTH, textAlign: 'center' }}>
          {getPaddedPositionString(position)}
        </td>
        {columnOrder.map((col) => (
          <td
            key={col.id}
            className={`ir-col-cell${col.id === 'asn' ? ' asn' : ''}${col.id === 'description' ? ' descriptionCell' : ''}`}
            style={{ width: columnWidths[col.id] || 120, textAlign: col.align || 'left' }}
          >
            {getCellValue(row, col)}
          </td>
        ))}
      </tr>
    );
  };

  const renderReadyForDropInteractiveRow = (index) => {
    const row = filteredReadyForDropData[index];
    const position = row && row.position ? row.position : READY_FOR_DROP_ROWS_COUNT - index;
    const stationLabel = index === READY_FOR_DROP_ROWS_COUNT - 1 ? 'LineUp' : <>&nbsp;</>;


    const conditionalBorderClass = index + 1 > 0 && (index + 1) % 5 === 0 && 'borderBt';

    return (
      <tr className={conditionalBorderClass || ''} key={index} data-testid={`select-row-${index}`}>
        <td className="ir-col-fixed ir-col-cell" style={{ width: FIXED_STATION_WIDTH, textAlign: 'center' }}>
          {stationLabel}
        </td>
        <td className="ir-col-fixed ir-col-cell" style={{ width: FIXED_SEQ_WIDTH, textAlign: 'center' }}>
          {getPaddedPositionString(position)}
        </td>
        {columnOrder.map((col) => (
          <td
            key={col.id}
            className={`ir-col-cell${col.id === 'asn' ? ' asn' : ''}${col.id === 'description' ? ' descriptionCell' : ''}`}
            style={{ width: columnWidths[col.id] || 120, textAlign: col.align || 'left' }}
          >
            {getCellValue(row, col)}
          </td>
        ))}
      </tr>
    );
  };

  const renderDroppedInteractiveRow = (index) => {
    const row = filteredDroppedData[index];
    const stationLabel = index === 0 ? 'Station' : <>&nbsp;</>;
    return renderInteractiveRow(row, index, stationLabel);
  };

  const totalColCount = 2 + columnOrder.length;

  return (
    <div>
      <div>
        <table
          width="98%"
          border="0"
          align="center"
          cellpadding="0"
          cellspacing="0"
          className="display_table"
        >
          <tr>
            <td>
              <div className="ir-toolbar">
                  <div className="ir-toolbar__item ir-toolbar__liveUpdate">
                    <span className="thead">LIVE UPDATE</span>
                    <strong className="date">
                      {moment().format('DD MMMM').toUpperCase()}{' '}
                      {moment().format('YYYY')}
                    </strong>
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__title">
                    <span className="thead">Production Broadcast</span>
                    <strong>{location?.state?.subAssembly ? `${location.state.subAssembly} Dropping Sequence` : 'Frame Dropping Sequence'}</strong>
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__line">
                    <span className="filterLabel">Select Line</span>
                    <LineDropdownSelector isDarkStyle={true} />
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__status">
                    <span className="filterLabel">Status</span>
                    <Select
                      value={selectedStatus}
                      onChange={handleStatusChange}
                      options={statusFilterOptions}
                      placeholder="All"
                      isClearable
                      classNamePrefix="ir-status-filter"
                    />
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__seq">
                    <span className="filterLabel">From Seq</span>
                    <input
                      type="text"
                      className={`ir-seq-input${fromSeqError ? ' ir-seq-input--error' : ''}`}
                      value={selectedFromSequence}
                      onChange={handleFromSeqChange}
                      placeholder="0-99"
                      disabled={selectedStatus?.value === 'READY_FOR_DROP'}
                      data-testid="from-seq-input"
                    />
                    {fromSeqError && (
                      <span className="ir-seq-error-msg">{fromSeqError}</span>
                    )}
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__seq">
                    <span className="filterLabel">To Seq</span>
                    <input
                      type="text"
                      className={`ir-seq-input${toSeqError ? ' ir-seq-input--error' : ''}`}
                      value={selectedToSequence}
                      onChange={handleToSeqChange}
                      placeholder="0-99"
                      disabled={selectedStatus?.value === 'READY_FOR_DROP'}
                      data-testid="to-seq-input"
                    />
                    {toSeqError && (
                      <span className="ir-seq-error-msg">{toSeqError}</span>
                    )}
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__buttons">
                    <div className="buttonsContainer">
                      <button
                        className="searchFilterBtn"
                        onClick={handleSearch}
                        title="Search"
                      >
                        Search
                      </button>
                      {isFilterActive && (
                        <button
                          className="clearFilterBtn"
                          onClick={handleClearFilters}
                          title="Clear all filters"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__attributes">
                    <AttributesSelector
                      columns={optionalColumns}
                      selectedOptionalCols={selectedOptionalColNames}
                      applyColumnsSelection={(cols) => setSelectedOptionalColNames(cols)}
                    />
                    <button className="ir-col-reset-btn" onClick={handleResetColumns} title="Reset column widths and order">
                      Reset Columns
                    </button>
                  </div>
                  <div className="ir-toolbar__item ir-toolbar__logo">
                    <Link to="/">
                      <img
                        src={Ipms}
                        alt="IPMS"
                        width="100%"
                        className="logo_img"
                      />
                    </Link>
                  </div>
              </div>
              <table
                ref={tableRef}
                className="ir-interactive-table"
                style={{ tableLayout: 'fixed' }}
              >
                <colgroup>
                  <col style={{ width: FIXED_STATION_WIDTH }} />
                  <col style={{ width: FIXED_SEQ_WIDTH }} />
                  {columnOrder.map((col) => (
                    <col key={col.id} style={{ width: columnWidths[col.id] || 120 }} />
                  ))}
                </colgroup>
                {appliedStatus !== 'DROPPED' && (
                  <>
                    <thead>
                      <tr>
                        <td
                          colSpan={totalColCount}
                          align="left"
                          valign="middle"
                          className="droprow"
                        >
                          Ready For Drop
                        </td>
                      </tr>
                      {renderInteractiveHeader()}
                    </thead>
                    <tbody>
                      {filteredReadyForDropData.length > 0
                        ? [...Array(READY_FOR_DROP_ROWS_COUNT).keys()].map(index =>
                            renderReadyForDropInteractiveRow(index),
                          )
                        : <tr><td colSpan={totalColCount} align="center" className="noDataCell">No data</td></tr>
                      }
                    </tbody>
                  </>
                )}

                {appliedStatus !== 'READY_FOR_DROP' && (
                  <>
                    <thead>
                      <tr>
                        <td
                          colSpan={totalColCount}
                          align="left"
                          valign="middle"
                          className="droprow droppedHeading"
                        >
                          DROPPED CHASSIS
                        </td>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDroppedData.length > 0
                        ? [...Array(FILTERED_DROPPED_ROWS_COUNT).keys()].map(renderDroppedInteractiveRow)
                        : <tr><td colSpan={totalColCount} align="center" className="noDataCell">No data</td></tr>
                      }
                    </tbody>
                  </>
                )}
              </table>
            </td>
          </tr>
          <tr align="center" valign="middle">
            <td
              height="60"
              className="footerCell"
            >
              Last Refreshed:{' '}
              {lastRefreshedDate
                ? lastRefreshedDate.format('hh:mmA [(IST)] DD MMMM YYYY')
                : '--:--'}
            </td>
          </tr>
        </table>
      </div>
    </div>
  );
};

export default InformationRadiator;

